import type { InputState } from './InputHandler';
import type { Role } from '@shared/types';

// ============================================================
// Mobile touch controls — virtual joystick + action buttons
// ============================================================

const JOYSTICK_RADIUS = 50;
const JOYSTICK_DEAD_ZONE = 8;
const LOCK_JOYSTICK_RADIUS = 35;

export function isTouchDevice(): boolean {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
}

interface TouchJoystick {
  touchId: number | null;
  originX: number;
  originY: number;
  currentX: number;
  currentY: number;
  active: boolean;
}

export class TouchControls {
  private readonly container: HTMLElement;
  private readonly role: Role;

  // Left joystick — steering
  private readonly steerJoystick: TouchJoystick = {
    touchId: null, originX: 0, originY: 0, currentX: 0, currentY: 0, active: false,
  };

  // Lock mode joystick (above-left of main joystick)
  private readonly lockJoystick: TouchJoystick = {
    touchId: null, originX: 0, originY: 0, currentX: 0, currentY: 0, active: false,
  };

  // Buttons state
  private firePressed = false;
  private boostFlarePressed = false;
  private barrelLeftPressed = false;
  private barrelRightPressed = false;
  private fireTouchId: number | null = null;
  private boostFlareTouchId: number | null = null;
  private barrelLeftTouchId: number | null = null;
  private barrelRightTouchId: number | null = null;

  // DOM elements
  private steerBase!: HTMLElement;
  private steerThumb!: HTMLElement;
  private lockBtn!: HTMLElement;
  private lockThumb!: HTMLElement;
  private fireBtn!: HTMLElement;
  private boostFlareBtn!: HTMLElement;
  private barrelLeftBtn!: HTMLElement;
  private barrelRightBtn!: HTMLElement;

  constructor(role: Role) {
    this.role = role;
    this.buildUI();
  }

  updateRole(role: Role): void {
    this.role = role;
    // Update button labels
    this.fireBtn.textContent = role === 'f16' ? 'FIRE' : 'BOOST';
    this.boostFlareBtn.textContent = role === 'shahed' ? 'FLARE' : 'BRAKE';
    this.lockBtn.textContent = role === 'f16' ? 'LOCK' : 'BRK';
  }

  private buildUI(): void {
    this.container = document.createElement('div');
    this.container.id = 'touch-controls';
    this.container.style.cssText = `
      position: fixed; inset: 0; z-index: 50;
      pointer-events: none; touch-action: none;
      display: block;
    `;
    document.body.appendChild(this.container);

    this.createSteerJoystick();
    this.createLockButton();
    this.createActionButtons();
    this.createBarrelButtons();
    this.createSettingsButton();
    this.bindTouchEvents();
  }

  // ── Create UI ──────────────────────────────────────────

  private createSteerJoystick(): void {
    this.steerBase = this.makeCircle(JOYSTICK_RADIUS * 2, 'rgba(255,255,255,0.12)');
    this.steerBase.style.cssText += `
      position: absolute; bottom: 30px; left: 30px;
      pointer-events: auto; border: 2px solid rgba(255,255,255,0.25);
    `;
    this.steerThumb = this.makeCircle(40, 'rgba(255,255,255,0.4)');
    this.steerThumb.style.cssText += `
      position: absolute;
      top: 50%; left: 50%;
      transform: translate(-50%, -50%);
      pointer-events: none;
    `;
    this.steerBase.appendChild(this.steerThumb);
    this.container.appendChild(this.steerBase);
  }

  private createLockButton(): void {
    this.lockBtn = this.makeCircle(LOCK_JOYSTICK_RADIUS * 2, 'rgba(255,60,60,0.15)');
    this.lockBtn.style.cssText += `
      position: absolute; bottom: 50px; left: 150px;
      pointer-events: auto; border: 2px solid rgba(255,60,60,0.35);
      display: flex; align-items: center; justify-content: center;
      font-size: 10px; color: rgba(255,255,255,0.6);
      font-family: monospace; letter-spacing: 1px;
    `;
    this.lockBtn.textContent = this.role === 'f16' ? 'LOCK' : 'BRK';
    this.lockThumb = this.makeCircle(24, 'rgba(255,60,60,0.5)');
    this.lockThumb.style.cssText += `
      position: absolute;
      top: 50%; left: 50%;
      transform: translate(-50%, -50%);
      pointer-events: none;
      display: none;
    `;
    this.lockBtn.appendChild(this.lockThumb);
    this.container.appendChild(this.lockBtn);
  }

  private createActionButtons(): void {
    // Fire button (F-16: gun/missile, Drone: not used — boost instead)
    this.fireBtn = this.makeCircle(60, 'rgba(255,60,60,0.2)');
    this.fireBtn.style.cssText += `
      position: absolute; bottom: 30px; right: 30px;
      pointer-events: auto; border: 2px solid rgba(255,60,60,0.4);
      display: flex; align-items: center; justify-content: center;
      font-size: 12px; color: rgba(255,255,255,0.7);
      font-family: monospace; letter-spacing: 1px;
    `;
    this.fireBtn.textContent = this.role === 'f16' ? 'FIRE' : 'BOOST';
    this.container.appendChild(this.fireBtn);

    // Secondary button (F-16: not needed, Drone: flare)
    this.boostFlareBtn = this.makeCircle(50, 'rgba(255,200,50,0.15)');
    this.boostFlareBtn.style.cssText += `
      position: absolute; bottom: 110px; right: 40px;
      pointer-events: auto; border: 2px solid rgba(255,200,50,0.3);
      display: flex; align-items: center; justify-content: center;
      font-size: 11px; color: rgba(255,255,255,0.6);
      font-family: monospace; letter-spacing: 1px;
    `;
    this.boostFlareBtn.textContent = this.role === 'shahed' ? 'FLARE' : 'BRAKE';
    this.container.appendChild(this.boostFlareBtn);
  }

  private createBarrelButtons(): void {
    const style = `
      position: absolute; bottom: 150px;
      pointer-events: auto; border: 2px solid rgba(255,255,255,0.2);
      display: flex; align-items: center; justify-content: center;
      font-size: 16px; color: rgba(255,255,255,0.5);
      font-family: monospace;
    `;
    this.barrelLeftBtn = this.makeCircle(40, 'rgba(255,255,255,0.08)');
    this.barrelLeftBtn.style.cssText += style + 'left: 30px;';
    this.barrelLeftBtn.textContent = '\u21BA'; // ↺
    this.container.appendChild(this.barrelLeftBtn);

    this.barrelRightBtn = this.makeCircle(40, 'rgba(255,255,255,0.08)');
    this.barrelRightBtn.style.cssText += style + 'right: 30px;';
    this.barrelRightBtn.textContent = '\u21BB'; // ↻
    this.container.appendChild(this.barrelRightBtn);
  }

  private createSettingsButton(): void {
    const btn = document.createElement('div');
    btn.style.cssText = `
      position: absolute; top: 16px; right: 16px;
      width: 40px; height: 40px;
      pointer-events: auto;
      background: rgba(0,0,0,0.4);
      border-radius: 8px;
      display: flex; align-items: center; justify-content: center;
      flex-direction: column; gap: 4px;
      backdrop-filter: blur(4px);
    `;
    // Three lines (burger icon)
    for (let i = 0; i < 3; i++) {
      const line = document.createElement('div');
      line.style.cssText = 'width: 20px; height: 2px; background: rgba(255,255,255,0.7); border-radius: 1px;';
      btn.appendChild(line);
    }
    btn.addEventListener('touchstart', (e) => {
      e.preventDefault();
      e.stopPropagation();
      // Simulate ESC key to toggle settings
      window.dispatchEvent(new KeyboardEvent('keydown', { code: 'Escape', bubbles: true }));
    });
    this.container.appendChild(btn);
  }

  private makeCircle(size: number, bg: string): HTMLElement {
    const el = document.createElement('div');
    el.style.cssText = `
      width: ${size}px; height: ${size}px;
      border-radius: 50%; background: ${bg};
      user-select: none; -webkit-user-select: none;
    `;
    return el;
  }

  // ── Touch events ───────────────────────────────────────

  private bindTouchEvents(): void {
    // Prevent scrolling/zooming globally
    document.addEventListener('touchmove', (e) => e.preventDefault(), { passive: false });

    // All touch handling goes through document — we match by touch ID
    document.addEventListener('touchstart', (e) => {
      if (e.touches.length > 1) e.preventDefault();
      for (let i = 0; i < e.changedTouches.length; i++) {
        const touch = e.changedTouches[i];
        const target = document.elementFromPoint(touch.clientX, touch.clientY);

        if (target === this.steerBase || this.steerBase.contains(target)) {
          this.startJoystick(touch, this.steerJoystick, this.steerBase);
        } else if (target === this.lockBtn || this.lockBtn.contains(target)) {
          this.startJoystick(touch, this.lockJoystick, this.lockBtn);
          this.lockThumb.style.display = 'block';
        } else if (target === this.fireBtn) {
          this.firePressed = true;
          this.fireTouchId = touch.identifier;
          this.fireBtn.style.background = 'rgba(255,60,60,0.5)';
        } else if (target === this.boostFlareBtn) {
          this.boostFlarePressed = true;
          this.boostFlareTouchId = touch.identifier;
          this.boostFlareBtn.style.background = 'rgba(255,200,50,0.4)';
        } else if (target === this.barrelLeftBtn) {
          this.barrelLeftPressed = true;
          this.barrelLeftTouchId = touch.identifier;
        } else if (target === this.barrelRightBtn) {
          this.barrelRightPressed = true;
          this.barrelRightTouchId = touch.identifier;
        }
      }
    }, { passive: false });

    document.addEventListener('touchmove', (e) => {
      for (let i = 0; i < e.changedTouches.length; i++) {
        const touch = e.changedTouches[i];
        this.moveJoystick(touch, this.steerJoystick, JOYSTICK_RADIUS, this.steerThumb);
        this.moveJoystick(touch, this.lockJoystick, LOCK_JOYSTICK_RADIUS, this.lockThumb);
      }
    }, { passive: false });

    const onTouchEnd = (e: TouchEvent) => {
      for (let i = 0; i < e.changedTouches.length; i++) {
        const touch = e.changedTouches[i];
        const id = touch.identifier;

        if (this.steerJoystick.touchId === id) {
          this.endJoystick(this.steerJoystick, this.steerThumb);
        }
        if (this.lockJoystick.touchId === id) {
          this.endJoystick(this.lockJoystick, this.lockThumb);
          this.lockThumb.style.display = 'none';
        }
        if (this.fireTouchId === id) {
          this.firePressed = false;
          this.fireTouchId = null;
          this.fireBtn.style.background = 'rgba(255,60,60,0.2)';
        }
        if (this.boostFlareTouchId === id) {
          this.boostFlarePressed = false;
          this.boostFlareTouchId = null;
          this.boostFlareBtn.style.background = 'rgba(255,200,50,0.15)';
        }
        if (this.barrelLeftTouchId === id) {
          this.barrelLeftPressed = false;
          this.barrelLeftTouchId = null;
        }
        if (this.barrelRightTouchId === id) {
          this.barrelRightPressed = false;
          this.barrelRightTouchId = null;
        }
      }
    };

    document.addEventListener('touchend', onTouchEnd);
    document.addEventListener('touchcancel', onTouchEnd);
  }

  private startJoystick(touch: Touch, joy: TouchJoystick, base: HTMLElement): void {
    const rect = base.getBoundingClientRect();
    joy.touchId = touch.identifier;
    joy.originX = rect.left + rect.width / 2;
    joy.originY = rect.top + rect.height / 2;
    joy.currentX = touch.clientX;
    joy.currentY = touch.clientY;
    joy.active = true;
  }

  private moveJoystick(touch: Touch, joy: TouchJoystick, radius: number, thumb: HTMLElement): void {
    if (!joy.active || touch.identifier !== joy.touchId) return;
    joy.currentX = touch.clientX;
    joy.currentY = touch.clientY;
    let dx = joy.currentX - joy.originX;
    let dy = joy.currentY - joy.originY;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist > radius) {
      dx = (dx / dist) * radius;
      dy = (dy / dist) * radius;
    }
    thumb.style.transform = `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px))`;
  }

  private endJoystick(joy: TouchJoystick, thumb: HTMLElement): void {
    joy.active = false;
    joy.touchId = null;
    joy.currentX = joy.originX;
    joy.currentY = joy.originY;
    thumb.style.transform = 'translate(-50%, -50%)';
  }


  // ── Get state ──────────────────────────────────────────

  private getJoystickAxis(joy: TouchJoystick, radius: number): { x: number; y: number } {
    if (!joy.active) return { x: 0, y: 0 };
    let dx = joy.currentX - joy.originX;
    let dy = joy.currentY - joy.originY;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist < JOYSTICK_DEAD_ZONE) return { x: 0, y: 0 };
    if (dist > radius) {
      dx = (dx / dist) * radius;
      dy = (dy / dist) * radius;
    }
    return { x: dx / radius, y: dy / radius };
  }

  getState(): InputState {
    const steer = this.getJoystickAxis(this.steerJoystick, JOYSTICK_RADIUS);
    const lock = this.getJoystickAxis(this.lockJoystick, LOCK_JOYSTICK_RADIUS);

    const lockActive = this.lockJoystick.active;

    if (this.role === 'f16') {
      return {
        forward: steer.y < -0.3,
        backward: steer.y > 0.3,
        left: steer.x < -0.3,
        right: steer.x > 0.3,
        up: this.firePressed,        // fire gun
        down: this.boostFlarePressed, // brake
        mouseX: steer.x * 0.8,
        mouseY: steer.y * 0.8,
        fireMissile: this.firePressed && lockActive, // fire missile when lock active
        fireGun: false,
        boost: this.barrelRightPressed,
        flare: this.barrelLeftPressed,
        lockMode: lockActive,
        deployFlare: false,
      };
    }

    // Drone
    return {
      forward: steer.y < -0.3,
      backward: steer.y > 0.3,
      left: steer.x < -0.3,
      right: steer.x > 0.3,
      up: this.firePressed,           // boost
      down: lockActive,               // brake (lock button = brake for drone)
      mouseX: steer.x * 0.8,
      mouseY: steer.y * 0.8,
      fireMissile: false,
      fireGun: false,
      boost: this.barrelRightPressed,
      flare: this.barrelLeftPressed,
      lockMode: false,
      deployFlare: this.boostFlarePressed, // flare
    };
  }

  dispose(): void {
    this.container.remove();
  }
}
